/* eslint-disable @next/next/link-passhref */
import { segment } from '@actions/analytics';
import Image from '@base/Image';
import { BANNER_CLICKED, BANNER_VIEWED } from '@constants/segment';
import styled from '@emotion/styled';
import useFirstRenderSkip from '@hooks/useFirstRenderSkip';
import useGoalEventProps from '@hooks/useGoalEventProps';
import useWindowSize from '@hooks/useWindowSize';
import { mediaQuery, Spacings } from '@styles/index';
import { ArrowButton } from 'aquilla/core/molecules/Carousel';
import { useRouter } from 'next/router';
import React, { useEffect, useRef } from 'react';
import { GET_STARTED } from './constants';
import useSwipeDetect from './hooks/useSwipeDetect';

const SecContainer = styled.div`
  width: 100%;
  overflow: hidden;
`;

const CarouselContainer = styled.div`
  width: var(--layout-width);
  position: relative;
  margin: auto;

  ${mediaQuery.uptoMobile} {
    width: var(--layout-mweb-width);
  }
`;

const Carousel = styled.div`
  display: flex;
  flex-direction: row;
  grid-gap: 16px;
  height: 100%;
  width: 100%;
  background-color: yellow;
`;

const CarouseItem = styled.div`
  width: var(--layout-width);
  width: 1136px;
  aspect-ratio: 10/3;
  background-color: var(--color-i-violet-dark-1);
  flex: 1 0 var(--layout-width);
  border-radius: ${Spacings.SPACING_2B};
  overflow: hidden;

  ${mediaQuery.uptoMobile} {
    width: 328px;
    aspect-ratio: 250/89;
    flex: 1 0 var(--layout-mweb-width);
  }
`;

const StyledArrowButton = styled(ArrowButton)`
  z-index: 1;
  position: absolute;
  top: calc(50% - ${(props) => props.theme.measurements.spacing(8)});
  left: ${(props) =>
    props.type === 'prev' ? `${Spacings.SPACING_4B}` : 'initial'};
  right: ${(props) =>
    props.type === 'next' ? `${Spacings.SPACING_4B}` : 'initial'};
  border: 1px solid var(--color-i-divider);
`;

const ScreenWrapper = styled.div`
  height: 100%;
  &.desktop {
    display: block;
    ${mediaQuery.uptoMobile} {
      display: none;
    }
  }
  &.mobile {
    display: none;
    ${mediaQuery.uptoMobile} {
      display: block;
    }
  }
`;

const StyledAnchor = styled.a`
  display: block;
`;

const getPaddedArray = (array) => {
  const midIndex = Math.floor(array.length / 2);
  if (array.length === 1) return array;
  if (array.length >= 5) {
    return [...array.slice(midIndex + 1), ...array.slice(0, midIndex + 1)];
  }
  const paddedArray = [];
  for (let i = 0; i < Math.max(6, array.length * 2); ) {
    paddedArray.push(array[i % array.length]);
    i += 1;
  }
  return paddedArray;
};

const CarouselFeed = ({ data, className }) => {
  const { data: actualFeeds = [] } = data;
  const {
    query: { goalUID }
  } = useRouter();
  const { isMobile } = useWindowSize();

  const feeds = getPaddedArray(actualFeeds.bannerImages);
  const midSlideIndex =
    feeds.length % 2 === 0 &&
    (actualFeeds.length >= 5 || actualFeeds.length === 2)
      ? Math.floor(feeds.length / 2) - 1
      : Math.floor(feeds.length / 2);
  const carouseRef = useRef(null);
  const goalSegmentData = useGoalEventProps({ goalUID });
  const direction = useRef(null);
  const hideNavButton = feeds.length === 1;
  const bannerViewEventDone = useRef(false);

  const bannerViewed = () => {
    if (!bannerViewEventDone.current) {
      segment.track(BANNER_VIEWED, {
        ...goalSegmentData
      });
      bannerViewEventDone.current = true;
    }
  };
  const callback = (entries) => {
    entries.forEach((entry) => {
      if (entry.intersectionRatio === 1) {
        bannerViewed();
      }
    });
  };
  useEffect(() => {
    const options = {
      rootMargin: '0px',
      threshold: 1.0
    };

    const observer = new IntersectionObserver(callback, options);
    const target = document.getElementById(GET_STARTED);

    observer.observe(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    bannerViewEventDone.current = false;
  }, [goalUID]);

  useFirstRenderSkip(() => {
    bannerViewed();
  }, [goalSegmentData, bannerViewEventDone.current]);
  const bannerClicked = (e, banner_url, uid) => {
    segment.track(BANNER_CLICKED, {
      ...goalSegmentData,
      url: banner_url,
      banner_uid: uid
    });
  };

  useEffect(() => {
    carouseRef.current.style.transform = `translateX(calc(-${midSlideIndex} * (100% + 16px)))`;
  }, [midSlideIndex]);

  useEffect(() => {
    carouseRef.current.addEventListener('transitionend', () => {
      if (direction.current === 'right') {
        const head = carouseRef.current.firstChild;
        carouseRef.current.removeChild(head);
        carouseRef.current.append(head);
      } else if (direction.current === 'left') {
        const tail = carouseRef.current.lastChild;
        carouseRef.current.removeChild(tail);
        carouseRef.current.prepend(tail);
      }
      carouseRef.current.style.transition = '';
      carouseRef.current.style.transform = `translateX(calc(-${midSlideIndex} * (100% + 16px)))`;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTransition = () => {
    if (direction.current === 'right') {
      carouseRef.current.style.transition =
        'transform 0.5s cubic-bezier(0.2, 1, 0.3, 1) 0s';
      carouseRef.current.style.transform = `translateX(calc(-${
        midSlideIndex + 1
      } * (100% + 16px)))`;
    } else if (direction.current === 'left') {
      carouseRef.current.style.transition =
        'transform 0.5s cubic-bezier(0.2, 1, 0.3, 1) 0s';
      carouseRef.current.style.transform = `translateX(calc(-${
        midSlideIndex - 1
      } * (100% + 16px)))`;
    }
  };
  const handleNext = () => {
    direction.current = 'right';
    handleTransition();
  };

  const handlePrev = () => {
    direction.current = 'left';
    handleTransition();
  };

  useSwipeDetect(
    carouseRef.current,
    (dir) => {
      if (isMobile) {
        if (dir === 'left') {
          handleNext();
        } else if (dir === 'right') {
          handlePrev();
        }
      }
    },
    true
  );
  return (
    <SecContainer id={GET_STARTED} className={className}>
      <CarouselContainer>
        {!hideNavButton ? (
          <StyledArrowButton type="prev" onClick={handlePrev} />
        ) : (
          <></>
        )}
        <Carousel ref={carouseRef}>
          {feeds.map((feed, i) => {
            return (
              <CarouseItem
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                onClick={(e) => bannerClicked(e, feed.clickUrl, feed.uid)}
              >
                <ScreenWrapper className="desktop">
                  <StyledAnchor
                    rel="noopener noreferrer"
                    target="_blank"
                    href={feed.clickUrl}
                  >
                    <Image
                      width={1136}
                      height={340.8}
                      alt="IMAGE IMAge"
                      src={`${feed.bannerImage}?w=2200`}
                    />
                  </StyledAnchor>
                </ScreenWrapper>
                <ScreenWrapper className="mobile">
                  <StyledAnchor
                    rel="noopener noreferrer"
                    target="_blank"
                    href={feed.clickUrl}
                  >
                    <Image
                      width={328}
                      height={116.768}
                      alt=""
                      src={feed.bannerAppImage}
                    />
                  </StyledAnchor>
                </ScreenWrapper>
              </CarouseItem>
            );
          })}
        </Carousel>
        {!hideNavButton ? (
          <StyledArrowButton type="next" onClick={handleNext} />
        ) : (
          <></>
        )}
      </CarouselContainer>
    </SecContainer>
  );
};

export default CarouselFeed;
